import React, { useEffect, useState } from 'react';
import { Box, Text } from 'grommet';
import styled, { css } from 'styled-components';
import { isMacOS } from '../utils/browser';

const defaultStyles = css`
    background: ${props => props.theme.global.colors['background-contrast']?.light || '#e8e8e8'};
    border: 1px solid ${props => props.theme.global.colors['border']?.light || '#ccc'};
    color: ${props => props.theme.global.colors['text-weak']?.light || '#666'};
    box-shadow: 0 1px 0 ${props => props.theme.global.colors['border']?.light || '#ccc'};
`;

const primaryStyles = css`
    background: rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: rgba(0, 0, 0, 0.7);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
`;

const KeyBox = styled(Box)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Mono', Monaco, monospace;
    ${props => props.$variant === 'primary' ? primaryStyles : defaultStyles}
`;

/**
 * Renders keyboard shortcut keys in a styled format
 *
 * @param {Object} props
 * @param {string|string[]} props.keys - Single key or array of keys to display
 * @param {boolean} props.inline - Whether to render inline with other content
 * @param {string} props.size - Size of the keys ('small', 'medium')
 * @param {string} props.variant - Visual variant ('default', 'primary' for use on primary buttons)
 *
 * Examples:
 *   <KeyboardShortcut keys="K" /> - renders [K]
 *   <KeyboardShortcut keys={['Cmd', 'K']} /> - renders [Cmd] [K] or [Ctrl] [K] depending on OS
 *   <KeyboardShortcut keys="ArrowLeft" /> - renders [←]
 *   <KeyboardShortcut keys={['Cmd', 'Enter']} variant="primary" /> - for use on primary buttons
 */
const KeyboardShortcut = ({ keys, inline = true, size = 'small', variant = 'default' }) => {
    const keyArray = Array.isArray(keys) ? keys : [keys];

    /*
        The platform is unknowable on the server, so this used to render "Ctrl" there and
        "⌘" on a Mac as soon as it hydrated. React treats that as a failed hydration and
        throws away the whole server-rendered tree to re-render on the client. This sits in
        the sidebar's search item, so it happened on every authenticated page and undid the
        benefit of server-rendering them at all.

        Starting non-Mac and correcting after mount keeps the first client render identical
        to the server's. Mac users see "Ctrl" for one frame, which is the price of hydrating
        the rest of the page instead of rebuilding it.
    */
    const [isMac, setIsMac] = useState(false);
    useEffect(() => { setIsMac(isMacOS()); }, []);

    // Map special keys to display symbols
    const getKeyDisplay = (key) => {

        const keyMap = {
            'Cmd': isMac ? '⌘' : 'Ctrl',
            'Command': isMac ? '⌘' : 'Ctrl',
            'Ctrl': isMac ? '⌃' : 'Ctrl',
            'Control': isMac ? '⌃' : 'Ctrl',
            'Alt': isMac ? '⌥' : 'Alt',
            'Option': isMac ? '⌥' : 'Alt',
            'Shift': isMac ? '⇧' : 'Shift',
            'Enter': '↵',
            'Return': '↵',
            'Tab': '⇥',
            'Escape': 'Esc',
            'Esc': 'Esc',
            'ArrowLeft': '←',
            'ArrowRight': '→',
            'ArrowUp': '↑',
            'ArrowDown': '↓',
            'Left': '←',
            'Right': '→',
            'Up': '↑',
            'Down': '↓',
            'Backspace': '⌫',
            'Delete': '⌦',
            'Space': '␣',
        };

        return keyMap[key] || key;
    };

    const showPlus = variant === 'primary' && keyArray.length > 1;

    return (
        <Box
            direction="row"
            gap="xxsmall"
            align="center"
            style={{ display: inline ? 'inline-flex' : 'flex' }}
        >
            {keyArray.map((key, index) => (
                <React.Fragment key={index}>
                    <KeyBox size={size} $variant={variant}>
                        <Text size="xsmall" weight={500}>
                            {getKeyDisplay(key)}
                        </Text>
                    </KeyBox>
                    {showPlus && index < keyArray.length - 1 && (
                        <Text size="xsmall" style={{ opacity: 0.6, marginLeft: '2px' }}>+</Text>
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
};

/**
 * Helper component for common keyboard shortcuts in tooltips
 */
export const ShortcutHint = ({ label, keys }) => (
    <Box direction="row" gap="xsmall" align="center">
        <Text size="small">{label}</Text>
        <KeyboardShortcut keys={keys} />
    </Box>
);

export default KeyboardShortcut;
